// People with access to the home (doc 17). The account holder invites remote users and grants extras.
import { api } from "../lib/api";
import { brand } from "../lib/brand";
import { t } from "../lib/i18n";
import { h, invalidate, resource } from "../lib/ui";
import {
  act, Async, Button, centsToInput, Chip, confirmDialog, Dialog, Field, formData, isBusy, openDialog, showLinkDialog, toast, toCents, Toggle,
} from "../shared/components";

function inviteDialog(homeId: string) {
  openDialog((close) => (
    <Dialog title={t("customer.people.inviteTitle")} onClose={close}>
      <form class="stack" onSubmit={(e: Event) => {
        const d = formData(e);
        act("people.invite", async () => {
          const res = await api.post(`/api/my/homes/${homeId}/members`, { name: d.name, email: d.email });
          close();
          invalidate(`my.people.${homeId}`);
          if (res.emailed) toast(t("customer.people.emailed", { email: d.email }));
          else if (res.inviteUrl) showLinkDialog(t("customer.people.linkTitle"), res.inviteUrl, res.status === "pending_consent" ? t("customer.people.linkBodyConsent", { name: d.name }) : t("customer.people.linkBody", { name: d.name }));
          else toast(t("customer.people.addedExisting", { name: d.name }));
        });
      }}>
        <p class="small muted">{t("customer.people.inviteBody")}</p>
        <Field label={t("fields.name")} id="pi-name"><input class="input" id="pi-name" name="name" required /></Field>
        <Field label={t("fields.email")} id="pi-email"><input class="input" id="pi-email" name="email" type="email" required /></Field>
        <div class="row end"><Button kind="secondary" onClick={close}>{t("common.cancel")}</Button><Button type="submit" busy={isBusy("people.invite")}>{t("customer.people.invite")}</Button></div>
      </form>
    </Dialog>
  ));
}

export function PeoplePage(props: { homeId: string }) {
  const dash = resource(`my.dash.${props.homeId}`, () => api.get(`/api/my/homes/${props.homeId}/dashboard`));
  const r = resource(`my.people.${props.homeId}`, () => api.get(`/api/my/homes/${props.homeId}/members`));
  const perms = dash.data?.me?.perms ?? {};
  const update = (m: any, patch: any) => act(`m.${m.id}`, async () => {
    await api.patch(`/api/my/memberships/${m.id}`, patch);
    Object.assign(m, patch);
  }, t("common.saved"));
  const consent = (m: any, accept: boolean) => act(`consent.${m.id}`, async () => {
    await api.post(`/api/my/memberships/${m.id}/consent`, { accept });
    r.reload();
  });
  const remove = async (m: any) => {
    if (!(await confirmDialog({ title: t("customer.people.removeTitle"), body: t("customer.people.removeBody", { name: m.name }), confirm: t("common.remove"), danger: true }))) return;
    update(m, { status: "revoked" }).then(() => r.reload());
  };
  return (
    <div class="stack loose">
      <div class="row between">
        <div class="stack tight">
          <h1>{t("customer.people.heading")}</h1>
          <p class="muted small">{t("customer.people.intro")}</p>
        </div>
        {perms.manageMembers ? <Button icon="plus" onClick={() => inviteDialog(props.homeId)}>{t("customer.people.invite")}</Button> : null}
      </div>
      {Async(r, (rows: any[]) => (
        <div class="stack">
          {rows.map((m) => {
            const holder = m.roles.includes("account_holder");
            const remote = m.roles.includes("remote") && !m.roles.includes("resident") && !holder;
            return (
              <section class="panel panel-pad stack">
                <div class="row between top">
                  <div class="stack tight" style="gap:2px">
                    <span class="strong">{m.name}{m.is_me ? <span class="muted small"> ({t("common.you")})</span> : null}</span>
                    {m.email ? <span class="small muted">{m.email}</span> : null}
                    <div class="row" style="gap:6px;margin-top:4px">{m.roles.map((x: string) => <Chip kind="info" plain>{t(`homeRole.${x}`)}</Chip>)}</div>
                  </div>
                  {m.status !== "active" ? <Chip kind="accent">{t(`memberStatus.${m.status}`)}</Chip> : null}
                </div>
                {m.status === "pending_consent" && perms.consentMembers && !perms.manageMembers ? (
                  <div class="note stack tight">
                    <span class="small">{t("customer.people.consentAsk", { name: m.name })}</span>
                    <div class="row"><Button size="small" busy={isBusy(`consent.${m.id}`)} onClick={() => consent(m, true)}>{t("customer.people.consent")}</Button><Button size="small" kind="secondary" onClick={() => consent(m, false)}>{t("customer.people.refuse")}</Button></div>
                  </div>
                ) : null}
                {perms.manageMembers && !holder ? (
                  <div class="stack tight">
                    <div class="row between"><span class="small">{t("customer.people.bodycam", { app: brand.current.appName })}</span><Toggle checked={m.bodycam_access} onChange={(v) => update(m, { bodycam_access: v })} /></div>
                    {remote ? <div class="row between"><span class="small">{t("customer.people.seeCosts")}</span><Toggle checked={m.can_view_costs} onChange={(v) => update(m, { can_view_costs: v })} /></div> : null}
                    {remote ? <div class="row between"><span class="small">{t("customer.people.approveCosts")}</span><Toggle checked={m.can_approve_costs} onChange={(v) => update(m, { can_approve_costs: v })} /></div> : null}
                    {(remote && m.can_approve_costs) || m.roles.includes("resident") ? (
                      <div class="row between">
                        <span class="small">{t("customer.people.limit")}</span>
                        <div class="input-affix" style="width:150px">
                          <span class="affix">$</span>
                          <input class="input tabular" inputMode="decimal" value={centsToInput(m.approval_limit_cents)} placeholder={t("customer.people.limitDefault")} aria-label={t("customer.people.limit")}
                            onChange={(e: Event) => update(m, { approval_limit_cents: toCents((e.target as HTMLInputElement).value) })} />
                        </div>
                      </div>
                    ) : null}
                    <div class="row end"><Button kind="ghost" size="small" onClick={() => remove(m)}>{t("customer.people.remove")}</Button></div>
                  </div>
                ) : holder ? <span class="xs muted">{t("customer.people.holderNote")}</span> : null}
              </section>
            );
          })}
        </div>
      ))}
      <p class="xs muted">{t("customer.people.privacy")}</p>
    </div>
  );
}
